import { FC } from 'react';
import { StyleSheet } from '@react-pdf/renderer';
import { CalendarDay, getLpPanelBgTextColors } from '../../utils';
import PageTemplate from '../Pdf/components/PageTemplate';
import Typography from '../Pdf/components/Typography';
import FirmLogo from '../Pdf/components/FirmLogo';
import Column from '../Pdf/components/Column';
import Row from '../Pdf/components/Row';
import Divider from './components/pdf/Divider';
import theme from '../Pdf/components/theme';
import getCoverLogoDimensions from './helpers/get-cover-logo-dimensions';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  textSection: {
    flex: 1,
    paddingVertical: '37.5px',
    paddingLeft: theme.spacing(theme.spacingSizes.xl),
    paddingRight: theme.spacing(theme.spacingSizes.xl),
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  logoSection: {
    flex: 1,
    paddingLeft: theme.spacing(theme.spacingSizes.xl),
    paddingRight: theme.spacing(theme.spacingSizes.xl),
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  title: {
    fontSize: '24px',
    lineHeight: '30px',
  },
});

type Props = {
  lightFirmLogoUrl: string;
  darkFirmLogoUrl: string;
  reportCoverLogoUrl?: string;
  reportCoverLogoAspectRatio?: number;
  panelColor: string;
  title: string;
  reportDate: CalendarDay;
  vehicleName: string;
  investorName: string;
};

const CoverPage: FC<Props> = ({
  lightFirmLogoUrl,
  darkFirmLogoUrl,
  reportCoverLogoUrl,
  reportCoverLogoAspectRatio,
  panelColor,
  title,
  reportDate,
  vehicleName,
  investorName,
}) => {
  const panelTextColors = getLpPanelBgTextColors(panelColor);
  const firmLogoUrl = reportCoverLogoUrl?.trim()
    ? reportCoverLogoUrl
    : panelTextColors.mainText === '#FFFFFF'
    ? lightFirmLogoUrl
    : darkFirmLogoUrl;

  const logoDimensions =
    reportCoverLogoUrl?.trim() && reportCoverLogoAspectRatio
      ? getCoverLogoDimensions(reportCoverLogoAspectRatio)
      : { width: undefined, height: 40 };

  return (
    <PageTemplate backgroundColor={panelColor}>
      <Column centered justify="center" style={styles.container}>
        <Row centered fullWidth>
          <Column style={styles.logoSection}>
            <FirmLogo
              url={firmLogoUrl}
              width={logoDimensions.width}
              height={logoDimensions.height}
            />
          </Column>
          <Divider
            orientation="vertical"
            style={{ backgroundColor: panelTextColors.divider }}
          />
          <Column spacing="xs" style={styles.textSection}>
            <Typography
              variant="h5"
              style={{ color: panelTextColors.subtleText }}
            >
              {vehicleName}
            </Typography>
            <Typography
              variant="h3"
              style={{ ...styles.title, color: panelTextColors.mainText }}
            >
              {title}
            </Typography>
            <Column spacing="xxs">
              <Typography
                variant="h6"
                style={{ color: panelTextColors.subtleText }}
              >
                {investorName}
              </Typography>
              <Typography
                variant="body2"
                style={{ color: panelTextColors.subtleText }}
              >
                {reportDate.toReadable()}
              </Typography>
            </Column>
          </Column>
        </Row>
      </Column>
    </PageTemplate>
  );
};

export default CoverPage;
